import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Alert,
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Skeleton,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconBuildingFactory2,
  IconCalendarStats,
  IconCreditCard,
  IconExternalLink,
  IconReceipt,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { httpRequest } from '@/shared/api/client';
import { EmptyState } from '@/shared/components/EmptyState';
import { PageHeader } from '@/shared/components/PageHeader';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';

export const Route = createFileRoute('/_app/billing')({ component: BillingPage });

interface SubscriptionInvoice {
  id: string;
  number?: string;
  date: string;
  amount: number;
  currency?: string;
  status: 'paid' | 'open' | 'past_due' | 'void' | string;
  hostedUrl?: string;
  pdfUrl?: string;
}

interface SubscriptionInfo {
  provider: 'stripe' | 'none' | string;
  customerPortalUrl?: string;
  paymentMethod?: { brand?: string; last4?: string; expMonth?: number; expYear?: number };
  invoices?: SubscriptionInvoice[];
}

async function fetchSubscription(): Promise<SubscriptionInfo | null> {
  try {
    return await httpRequest<SubscriptionInfo>('/api/billing/subscription', {
      endpointType: 'plain',
      retries: 0,
    });
  } catch {
    return null;
  }
}

function statusColor(s: string): string {
  if (s === 'paid') return 'green';
  if (s === 'open') return 'blue';
  if (s === 'past_due') return 'red';
  if (s === 'void') return 'gray';
  return 'gray';
}

function statusLabel(s: string): string {
  if (s === 'paid') return 'Pagata';
  if (s === 'open') return 'In attesa';
  if (s === 'past_due') return 'Scaduta';
  if (s === 'void') return 'Annullata';
  return s;
}

function BillingPage() {
  const subQ = useQuery({
    queryKey: ['billing-subscription'],
    queryFn: fetchSubscription,
    staleTime: 5 * 60_000,
  });

  if (subQ.isLoading) {
    return (
      <Stack>
        <Skeleton h={60} radius="md" />
        <Skeleton h={120} radius="md" />
        <Skeleton h={200} radius="md" />
      </Stack>
    );
  }
  if (subQ.isError) {
    return <QueryErrorAlert error={subQ.error} onRetry={() => void subQ.refetch()} />;
  }

  const sub = subQ.data;
  const stripeConnected = sub?.provider === 'stripe' && !!sub.customerPortalUrl;
  const invoices = sub?.invoices ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        title="Fatturazione"
        description="Sottoscrizione, metodo di pagamento e storico fatture del tuo abbonamento MaeFwk10."
      />

      {!stripeConnected ? (
        <Alert color="orange" variant="light" icon={<IconAlertCircle size={18} />} title="Stripe non ancora connesso">
          <Stack gap="xs">
            <Text size="sm">
              Su questo tenant non è ancora collegato un account Stripe. Le fatture e il metodo di
              pagamento saranno disponibili una volta completata l'integrazione lato BE
              (<code>/api/billing/subscription</code>).
            </Text>
            <Text size="sm">
              Nel frattempo, per la <strong>rendicontazione delle ore fatturabili</strong> ai clienti
              puoi usare la sezione{' '}
              <Anchor component={Link} to="/insights/monthly-invoices">Fatturato mensile</Anchor>.
            </Text>
          </Stack>
        </Alert>
      ) : (
        <Card withBorder padding="lg" radius="md">
          <Group justify="space-between" wrap="nowrap">
            <Group>
              <ThemeIcon size="xl" radius="md" variant="light" color="blue">
                <IconCreditCard size={24} />
              </ThemeIcon>
              <div>
                <Text fw={600}>Metodo di pagamento</Text>
                <Text size="sm" c="dimmed">
                  {sub?.paymentMethod
                    ? `${(sub.paymentMethod.brand ?? 'Carta').toUpperCase()} **** ${sub.paymentMethod.last4 ?? '????'} — scade ${String(sub.paymentMethod.expMonth ?? '?').padStart(2, '0')}/${sub.paymentMethod.expYear ?? '????'}`
                    : 'Nessun metodo configurato'}
                </Text>
              </div>
            </Group>
            <Button
              component="a"
              href={sub!.customerPortalUrl!}
              target="_blank"
              rel="noopener noreferrer"
              variant="light"
              rightSection={<IconExternalLink size={14} />}
            >
              Apri Customer Portal
            </Button>
          </Group>
        </Card>
      )}

      <Group justify="space-between" align="flex-end">
        <Title order={3}>Storico fatture</Title>
        {invoices.length > 0 && (
          <Badge variant="light" color="gray">{invoices.length} fatture</Badge>
        )}
      </Group>

      {invoices.length === 0 ? (
        <Card withBorder padding={0} radius="md">
          <EmptyState
            icon={IconReceipt}
            title={stripeConnected ? 'Nessuna fattura emessa' : 'Storico non disponibile'}
            description={stripeConnected
              ? 'Quando emetteremo la prossima fattura comparirà qui automaticamente.'
              : 'Lo storico fatture sarà disponibile dopo la connessione a Stripe.'}
          />
        </Card>
      ) : (
        <Card withBorder padding={0} radius="md">
          <Table verticalSpacing="sm" horizontalSpacing="md" highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Numero</Table.Th>
                <Table.Th>Data</Table.Th>
                <Table.Th ta="right">Importo</Table.Th>
                <Table.Th>Stato</Table.Th>
                <Table.Th></Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {invoices.map((inv) => (
                <Table.Tr key={inv.id}>
                  <Table.Td>
                    <Text size="sm" style={{ fontFamily: 'monospace' }}>
                      {inv.number ?? inv.id}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{dayjs(inv.date).format('DD/MM/YYYY')}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" fw={500}>
                      {new Intl.NumberFormat('it-IT', { style: 'currency', currency: inv.currency ?? 'EUR' })
                        .format(inv.amount)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Badge color={statusColor(inv.status)} variant={inv.status === 'paid' ? 'light' : 'filled'}>
                      {statusLabel(inv.status)}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      {inv.hostedUrl && (
                        <Button
                          component="a"
                          href={inv.hostedUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          variant="subtle"
                          rightSection={<IconExternalLink size={12} />}
                        >
                          Apri
                        </Button>
                      )}
                      {inv.pdfUrl && (
                        <Button
                          component="a"
                          href={inv.pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          size="xs"
                          variant="subtle"
                          leftSection={<IconReceipt size={12} />}
                        >
                          PDF
                        </Button>
                      )}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Card>
      )}

      <Card withBorder padding="md" radius="md">
        <Group>
          <ThemeIcon size="md" radius="md" variant="light" color="indigo">
            <IconBuildingFactory2 size={16} />
          </ThemeIcon>
          <div style={{ flex: 1 }}>
            <Text size="sm" fw={500}>Hai bisogno di una fattura intestata diversamente?</Text>
            <Text size="xs" c="dimmed">
              Per modifiche all'intestazione fiscale contatta{' '}
              <Anchor href="mailto:amministrazione@maestrale.it">amministrazione@maestrale.it</Anchor>.
            </Text>
          </div>
          <Button
            component={Link}
            to="/licensing"
            variant="subtle"
            leftSection={<IconCalendarStats size={14} />}
          >
            Vedi piano
          </Button>
        </Group>
      </Card>
    </Stack>
  );
}
